/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect } from 'react'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import * as _ from 'lodash'
import dayjs from 'dayjs'
import { message } from 'antd'
import uuid from 'uuid'

// icon
import { ArrowIco } from '@shared/Icon/ArrowIcon'
import { CommentsIco } from '@shared/Icon/EtcIcon'
import FavoriteActiveImg from '@assets/images/btn/favorite-manage-active.png'
// css
import { Button } from '@styles/button.style'
import {
  FavoritePickListBtn,
  PickBottomFloatingBar,
  PickFloatingBtn,
  PickTip,
} from '@views/Popup/CompanyPickDetail/index.style'
import useRootStore from '@store/useRootStore'
import * as Utils from '@utils/utils'
import { MarketStatusType, ProducerType } from '@game-type/betradar-type'
import { ENABLE_MIN_BET_RATE, makeSelectionValue } from '@game/util'
import { AdvCommentListItem } from '@components/AdvComment/AdvCommentListItem'
import { CompanyPickDetailContents } from './CompanyPickDetailContents'
import { FavoriteButton } from '@components/Button/FavoriteButton'
import { LikeButton } from '@components/Button/LikeButton'
import { AdvComments } from '@components/AdvComment/index.style'
import { COMMUNICATION_BLOCK_TYPE, FROM_VIEW_TYPE, USER_COMMENT_TYPE } from '@v2/constants/common'
import PopupUtils from '@utils/popup-utils'
import { Schema$UserComment, Schema$UserCommentExt } from '@interface/comment'
import { runInAction } from 'mobx'
import AdvImg from '@components/AdvImg'
import { GAME_TYPE_REGULAR_FILTER } from '@game-type/game-market-types'

const LIKE_ACTIVE_CLASS = 'active'
const FAVORITE_ACTIVE_CLASS = 'icon-btn active ani'

/**
 * 프리미엄 분석가 선택시 나오는 경기분석 팝업 컴포넌트
 * @constructor
 */

interface CompanyPickDetailPopupProps {
  managerUserPromiseId?: string
  doCloseMessage: () => void
}

export const CompanyPickDetailPopup: React.FC<CompanyPickDetailPopupProps> = observer((props) => {
  const { managerUserPromiseId } = props
  const {
    globalStore, companyPickStore, authStore, matchupStore,
    chattingStore, bettingHistoryStore, commentStore,
  } = useRootStore()
  const { favoriteTotalPreMatchCount = 0 } = matchupStore
  const {
    selectedCompanyPick,
    selectedCompanyPickRecentComments,
    isMatchupPageCall,
  } = companyPickStore
  const { userInfo } = authStore

  useEffect(() => {
    if (_.isEmpty(selectedCompanyPick)) {
      return
    }

    // 채팅 - 프리미엄 분석가 - 상세를 통해 진입한 경우 content 는 있으므로 호출할 필요가 없다.
    if (_.isEmpty(_.trim(selectedCompanyPick.content))) {
      companyPickStore.fetchCompanyPickContent().catch(err => console.error(err))
    }
    companyPickStore.fetchRecentComments().catch(err => console.error(err))

    if (authStore.isSignedIn && !selectedCompanyPick.isAlreadyRead) {
      companyPickStore.createCompanyPickFirstReadLog(selectedCompanyPick).catch(err => console.error(err))
    }
  }, [selectedCompanyPick])

  const callbackCreated = (comment: Schema$UserComment): void => {
    PopupUtils.changePopupTitle('pick-comments-list', `댓글 ${selectedCompanyPick.commentCount + 1}`)
    companyPickStore.createCommentCallback(comment)
  }
  const callbackDeleted = (comment: Schema$UserComment): void => {
    PopupUtils.changePopupTitle('pick-comments-list', `댓글 ${selectedCompanyPick.commentCount - 1}`)
    companyPickStore.deleteCommentCallback(comment)
  }

  const onShowContent = (isFocus: boolean): void => {
    bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.COMPANY_PICK)
    bettingHistoryStore.setBettingHistoryInitProps({ onlyMajorClub: true, fromViewType: FROM_VIEW_TYPE.COMPANY_PICK })
    commentStore.setCommentTopEditorFocus(isFocus, USER_COMMENT_TYPE.COMPANY_PICK_COMMENT)
    PopupUtils.showAdvComments({
      commentType: USER_COMMENT_TYPE.COMPANY_PICK_COMMENT,
      targetId: selectedCompanyPick.id,
      callbackCreated,
      callbackDeleted,
      callbackUpdated: companyPickStore.updateCommentCallback,
      callbackLiked: companyPickStore.updateAllCompanyPickCommentsLikeInfo,
      commentCount: selectedCompanyPick.commentCount,
      fromViewType: FROM_VIEW_TYPE.COMPANY_PICK_COMMENT,
    })
  }

  const onClickFavoriteCompanyPick = (): void => {
    companyPickStore.toggleCompanyPickLike(selectedCompanyPick, isMatchupPageCall)
      .catch(err => console.error(err))
  }

  const onClickFavoriteMatch = (): void => {
    if (!Utils.isNotStartedMatch(selectedCompanyPick.dateOfMatch)) {
      message.info('현재 찜할 수 없는 경기입니다.')
      return
    }
    companyPickStore.toggleFavoriteMatch(selectedCompanyPick).catch(err => console.error(err))
  }

  const onClickAddCartItem = (): void => {
    const { dateOfMatch, matchId, tournamentId, marketType, specifiers, selection, outcomeMetaId } = selectedCompanyPick

    if (dayjs().isAfter(dateOfMatch)) {
      props.doCloseMessage()
      Utils.showToast(Utils.TOAST_TYPE.WARN, '시작시간이 초과된 경기입니다.')
      return
    }

    if (matchupStore.existCartItem(matchId, marketType, specifiers, selection)) {
      props.doCloseMessage()
      Utils.showToast(Utils.TOAST_TYPE.WARN, '베팅슬립에 이미 선택한 경기가 있습니다.')
      return
    }

    const {
      market,
      outcome,
    } = matchupStore.getMarketAndOutcomeInTournamentTracks(matchId, tournamentId, marketType, specifiers, selection)
    if (_.isEmpty(market)
      || _.isEmpty(outcome)
      || market.state === MarketStatusType.Deactivated
      || market.state === MarketStatusType.Settled
      || market.state === MarketStatusType.HandedOver
      || market.state === MarketStatusType.Suspended
      || outcome.isActive === false
      || _.isNil(outcome.rate)
      || outcome.rate < ENABLE_MIN_BET_RATE) {
      Utils.showToast(Utils.TOAST_TYPE.WARN, '현재 베팅할 수 없는 경기입니다.')
      return
    }
    const value = makeSelectionValue({
      marketType,
      specifiers,
      outcomeType: selection,
      rate: outcome.rate,
      outcomeMetaId,
      key: selection,
      producerId: ProducerType.PREODDS,
    })
    matchupStore.setSelectedCartParams(tournamentId, matchId, value)

    _.includes(GAME_TYPE_REGULAR_FILTER.cross, marketType)
      ? matchupStore.addSelectedCartItem()
      : matchupStore.addSelectedDetailCartItem()
    props.doCloseMessage()
  }

  const onClickMoveToFavoriteMatchList = (): void => {
    if (favoriteTotalPreMatchCount < 1) {
      return
    }
    props.doCloseMessage()

    if (isMobile) {
      // 채팅 - 프리미엄 분석가 - 프리미엄 분석가 상세에서 다른 페이지로 이동할때만 채팅 모달을 닫는다.
      chattingStore.changeChattingModalVisible(false).catch(err => console.error(err))
      // 상세 BET - 채팅 - 프리미엄 분석가 알림 - 프리미엄 분석가 - 찜매치 이동시(페이지전환) 모든 모달을 닫는다.
      globalStore.cleanVisibleModalList()
    }
    companyPickStore.moveToFavoriteMatchList()
  }

  const onUserBlockSuccessHandler = (userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]) => {
    const isUserBlock = !_.isEmpty(blockTypes)
    const isCommentBlock = _.includes(blockTypes, COMMUNICATION_BLOCK_TYPE.PICK_COMMENT)

    runInAction(() => {
      companyPickStore.selectedCompanyPickRecentComments = commentStore.setCommentBlock(
        selectedCompanyPickRecentComments as Schema$UserCommentExt[],
        userPromiseId,
        isUserBlock,
        isCommentBlock,
      )
    })
  }

  if (_.isEmpty(selectedCompanyPick)) {
    return (<Fragment/>)
  }

  const favoriteMatchButtonClass = `${Utils.isNotStartedMatch(selectedCompanyPick.dateOfMatch) ? '' : 'disabled'} ${selectedCompanyPick.isFavoriteMatch ? FAVORITE_ACTIVE_CLASS : 'icon-btn'}`
  const FavoriteActiveBtn = <img src={FavoriteActiveImg} className="favorite-btn-active-img" alt="favorite-btn-active"/>

  return (
    <Fragment>
      <CompanyPickDetailContents companyPick={selectedCompanyPick} managerUserPromiseId={managerUserPromiseId}/>

      <PickTip>
        <span className="tip-box">TIP</span>하단 {FavoriteActiveBtn}을 이용하면 관심경기를 모아볼 수 있습니다.
      </PickTip>

      {/* 댓글 */}
      <AdvComments style={{ zIndex: 0}}>
        <Button
          TP
          onClick={() => {
            onShowContent(false)
          }}
          className="total-comments-btn">댓글 {selectedCompanyPick.commentCount}<ArrowIco/>
        </Button>
        <ul className="pick-comments-list">
          {_.map(selectedCompanyPickRecentComments, item => {
            return (
              <AdvCommentListItem
                key={uuid.v4()}
                userComment={item as Schema$UserCommentExt}
                hideReply
                callbackCreated={companyPickStore.createCommentCallback}
                callbackUpdated={companyPickStore.updateCommentCallback}
                callbackDeleted={companyPickStore.deleteCommentCallback}
                callbackLiked={companyPickStore.updateAllCompanyPickCommentsLikeInfo}
                fromViewType={FROM_VIEW_TYPE.COMPANY_PICK_COMMENT}
                onUserBlockSuccessHandler={onUserBlockSuccessHandler}
              />
            )
          })}
        </ul>
        <Button TP className="write-comments-area before no-bg" onClick={() => {
          onShowContent(true)
        }}>
          <div className="profile-img-wrap lhn27">
            {userInfo.customProfileImg ?
              (<AdvImg src={userInfo.avatar} srcMode={'small'} alt="사진첩 프로필" className="profile-img custom-img"/>)
              :
              (<AdvImg src={userInfo.avatar} srcMode={'small'} alt="스킨 프로필" className="profile-img"/>)
            }
          </div>
          <p className="txt">댓글을 남겨보세요</p>
        </Button>
        <span className="underline"/>
      </AdvComments>

      {/* 기획 수정으로 인해 잠시 주석처리 */}
      {/* 매치업 이동 버튼 */}
      {/*<Button gray css={GoMatchBtnStyle} className="btn-color">*/}
      {/*  매치업 이동<ArrowIco/>*/}
      {/*</Button>*/}

      {/* 하단 플로팅 영역 */}
      <PickBottomFloatingBar className={!isMobile && 'pc-pick-bottom-floating-bar'}>
        <ul className="pick-bar-list">
          <li className="pick-bar-list--item">
            <LikeButton
              id="todayPickLikeBtn"
              className={`ico-btn pick-bar-btn ${selectedCompanyPick.isLike ? LIKE_ACTIVE_CLASS : ''}`}
              count={selectedCompanyPick.likeCount}
              onClick={onClickFavoriteCompanyPick}
            />
            <Button TP className="ico-btn" onClick={() => {
              onShowContent(false)
            }}>
              <CommentsIco/>
              <span className="num">{selectedCompanyPick.commentCount}</span>
            </Button>
          </li>
          <li className="pick-bar-list--item">

            {/* TODO 찜 활성화되었을 시 'active ani' class 추가 / 비활성화시(경기시작이후) 'disabled' 추가 */}
            <FavoriteButton
              id="companyPickFavoriteBtn"
              className={favoriteMatchButtonClass}
              onClick={onClickFavoriteMatch}
              hideBg
            />

            {/* 기획 수정으로 인해 잠시 주석처리 */}
            {/*<Button TP onClick={onClickAddBet} className="add-btn">*/}
            {/*  <div className="plus-ico">*/}
            {/*    <span className="line line-1"/>*/}
            {/*    <span className="line line-2"/>*/}
            {/*  </div>*/}
            {/*  슬립추가*/}
            {/*</Button>*/}
          </li>
        </ul>
      </PickBottomFloatingBar>

      {/* TODO '찜 이동' 버튼 상시 호출 / 비활성화시 'disabled' 추가 */}
      <FavoritePickListBtn
        onClick={onClickMoveToFavoriteMatchList}
        className={selectedCompanyPick.isFavoriteMatch && 'active'}
      >
        <div className="go-favorite-match">
          <div className="btn-bg"/>
          {FavoriteActiveBtn}<span className="txt">목록으로 이동</span><ArrowIco/>
          {/*<span className="left-item"><FavoriteIco/>{favoriteMatchCount}</span>*/}
        </div>
      </FavoritePickListBtn>

      {/* TODO 매치업 리스트에서 프리미엄 분석가 버튼으로 들어온 경우 '슬립 추가' 버튼 호출 */}
      {isMatchupPageCall &&
        <PickFloatingBtn onClick={onClickAddCartItem} className="add-cart-btn">
          <Button className="pick-floating-btn">
            <span className="plus-ico"/>
            슬립
          </Button>
        </PickFloatingBtn>
      }
    </Fragment>
  )
})
